import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import InfoTip from './InfoTip';
import {
  Envelope,
  Tag,
  NumberSquareOne,
  ForkKnife,
  Heart,
  CalendarCheck,
  SpinnerGap,
  Download,
  PencilSimple,
  Trash,
  MagicWand,
  Image as ImageIcon,
  Warning,
  X,
  FilePdf,
  FileImage,
  CaretDown
} from '@phosphor-icons/react';
import api from '../services/api';
import { toast } from 'sonner';
import { resolveMediaUrl } from '../utils/media';
import useEventTypeConfig from '../hooks/useEventTypeConfig';
import AIPremiumButton, { AI_USAGE_IMAGE_GENERATION } from './AIPremiumGate';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

// Icon mapping for stationery types
const stationeryIcons = {
  wedding_invitation: Envelope,
  baby_shower_invitation: Envelope,
  birthday_invitation: Envelope,
  day_invitation: CalendarCheck,
  save_the_date: CalendarCheck,
  room_tag: Tag,
  table_number: NumberSquareOne,
  menu_card: ForkKnife,
  thank_you_card: Heart,
  rsvp_card: Envelope,
  itinerary_card: CalendarCheck
};

// Field labels for display
const fieldLabels = {
  bride_name: "Bride's Name",
  groom_name: "Groom's Name",
  host_names: 'Host Names',
  honoree_names: 'Celebrant Names',
  event_date: "Event Date",
  event_name: "Event Name",
  venue: "Venue",
  message: "Message",
  guest_name: "Guest Name",
  room_number: "Room Number",
  table_number: "Table Number",
  menu_items: "Menu Items",
  response_date: "Response Deadline",
  itinerary_event_name: "Event Name",
  itinerary_date: "Event Date",
  itinerary_time: "Time",
  itinerary_venue: "Venue",
  itinerary_dress_code: "Dress Code"
};

export default function StationeryGenerator({ eventId, currentTheme, eventData }) {
  const eventTypeConfig = useEventTypeConfig(eventData);
  const [availableTypes, setAvailableTypes] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [generatedItems, setGeneratedItems] = useState([]);
  const [customFields, setCustomFields] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Edit dialog state
  const [editingItem, setEditingItem] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [isRegenerating, setIsRegenerating] = useState(false);

  useEffect(() => {
    fetchStationeryData();
  }, [eventId]);

  const fetchStationeryData = async () => {
    try {
      const [typesRes, itemsRes] = await Promise.all([
        api.get('/stationery-types', { params: { event_type: eventData?.event_type } }),
        api.get(`/events/${eventId}/stationery`)
      ]);
      
      setAvailableTypes(typesRes.data);
      setGeneratedItems(itemsRes.data.items || []);
    } catch (error) {
      console.error('Error fetching stationery data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTypeToggle = (typeId) => {
    setSelectedTypes(prev => 
      prev.includes(typeId) 
        ? prev.filter(t => t !== typeId)
        : [...prev, typeId]
    );
  };

  const handleCheckboxClick = (event) => {
    event.stopPropagation();
  };

  const handleGenerate = async () => {
    if (selectedTypes.length === 0) {
      toast.error('Please select at least one stationery type');
      return;
    }

    if (!currentTheme?.selected_theme) {
      toast.error('Please select a theme first before generating stationery');
      return;
    }

    // First update couple details if provided
    if (eventTypeConfig.features.showCoupleDetailsInputs && (customFields.bride_name || customFields.groom_name)) {
      try {
        const formData = new FormData();
        if (customFields.bride_name) formData.append('bride_name', customFields.bride_name);
        if (customFields.groom_name) formData.append('groom_name', customFields.groom_name);
        await api.put(`/events/${eventId}/couple-details`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } catch (error) {
        console.error('Error updating couple details:', error);
      }
    }

    setIsGenerating(true);
    try {
      const formData = new FormData();
      selectedTypes.forEach(type => formData.append('stationery_types', type));
      formData.append('custom_fields', JSON.stringify(customFields));

      const response = await api.post(`/events/${eventId}/stationery/generate`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!Array.isArray(response.data.generated) || response.data.generated.length === 0) {
        toast.error('No stationery was generated. Please try again.');
        return;
      }
      toast.success(response.data.message);
      setGeneratedItems(prev => {
        const existingMap = new Map(prev.map(item => [item.type, item]));
        response.data.generated.forEach(item => existingMap.set(item.type, item));
        return Array.from(existingMap.values());
      });
      setSelectedTypes([]);
    } catch (error) {
      console.error('Error generating stationery:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate stationery');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await api.delete(`/events/${eventId}/stationery/${itemId}`);
      setGeneratedItems(prev => prev.filter(item => item.id !== itemId));
      toast.success('Stationery item deleted');
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete item');
    }
  };

  const handleDownload = async (item, format = 'png') => {
    try {
      const { image_url, name, id } = item;
      let downloadUrl;
      let filename;
      
      if (format === 'png') {
        // Original PNG - direct download
        downloadUrl = resolveMediaUrl(image_url, { preferProxy: true });
        filename = `${name.replace(/\s+/g, '_')}.png`;
      } else {
        // PDF or High-res PNG - use the new endpoint
        downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/events/${eventId}/stationery/${id}/download?format=${format}`;
        filename = format === 'pdf' 
          ? `${name.replace(/\s+/g, '_')}_print_ready.pdf`
          : `${name.replace(/\s+/g, '_')}_300dpi.png`;
      }
      
      // Get auth token (stored as 'access_token' by the auth system)
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(downloadUrl, {
        headers: token && format !== 'png' ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(`${format === 'pdf' ? 'PDF' : format === 'png-hires' ? 'High-res PNG' : 'PNG'} download started!`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download. Please try again.');
    }
  };

  // Open edit dialog
  const handleEditItem = (item) => {
    setEditingItem(item);
    setEditFields({ ...item.editable_fields });
  };

  // Close edit dialog
  const handleCloseEdit = () => {
    setEditingItem(null);
    setEditFields({});
  };

  // Save and regenerate with new fields
  const handleSaveAndRegenerate = async () => {
    if (!editingItem) return;

    setIsRegenerating(true);
    try {
      const formData = new FormData();
      formData.append('editable_fields', JSON.stringify(editFields));

      const response = await api.put(
        `/events/${eventId}/stationery/${editingItem.id}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      toast.success('Stationery regenerated with your changes!');
      
      // Update the item in the list
      setGeneratedItems(prev => 
        prev.map(item => 
          item.id === editingItem.id ? response.data.item : item
        )
      );
      
      handleCloseEdit();
    } catch (error) {
      console.error('Error updating stationery:', error);
      toast.error('Failed to regenerate stationery');
    } finally {
      setIsRegenerating(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <SpinnerGap className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const hasSelectedTheme = !!currentTheme?.selected_theme;

  return (
    <div className="space-y-6">
      {/* Warning if no theme selected */}
      {!hasSelectedTheme && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <Warning className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div>
                <p className="font-medium text-yellow-800">Theme Required</p>
                <p className="text-sm text-yellow-700">
                  {eventTypeConfig.labels.stationeryThemeRequired}
                  Go to the Theme & Branding section to choose your theme.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Select Stationery Types */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <MagicWand className="w-5 h-5" />
            {eventTypeConfig.labels.stationeryTitle}
            <InfoTip content={eventTypeConfig.labels.stationeryDescription} label={`About ${eventTypeConfig.labels.stationeryTitle}`} />
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {availableTypes.map((type) => {
              const Icon = stationeryIcons[type.id] || Envelope;
              const isSelected = selectedTypes.includes(type.id);
              const isGenerated = generatedItems.some(item => item.type === type.id);
              const checkboxId = `stationery-checkbox-${type.id}`;
              
              return (
                <div
                  key={type.id}
                  onClick={() => handleTypeToggle(type.id)}
                  className={`
                    p-4 rounded-lg border-2 cursor-pointer transition-all
                    ${isSelected ? 'border-[var(--theme-primary,#5D7061)] bg-[var(--theme-primary,#5D7061)]/5' : 'border-gray-200 hover:border-gray-300'}
                  `}
                  data-testid={`stationery-type-${type.id}`}
                >
                  <div className="flex items-start gap-3">
                    <Checkbox 
                      id={checkboxId}
                      checked={isSelected}
                      onClick={handleCheckboxClick}
                      onCheckedChange={() => handleTypeToggle(type.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-muted-foreground" />
                        <Label
                          htmlFor={checkboxId}
                          className="font-medium text-sm cursor-pointer"
                          onClick={handleCheckboxClick}
                        >
                          {type.name}
                        </Label>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {type.description}
                      </p>
                      {isGenerated && (
                        <Badge variant="secondary" className="mt-2 text-xs">
                          Generated
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Couple Details Input */}
          {selectedTypes.length > 0 && eventTypeConfig.features.showCoupleDetailsInputs && (
            <div className="border-t pt-4 mt-4">
              <div className="mb-3 flex items-center gap-1">
                <h4 className="font-medium">{eventTypeConfig.labels.stationeryDetailsTitle}</h4>
                <InfoTip content={eventTypeConfig.labels.stationeryDetailsDescription} label={`About ${eventTypeConfig.labels.stationeryDetailsTitle}`} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="bride_name">Bride&apos;s Name</Label>
                  <Input
                    id="bride_name"
                    value={customFields.bride_name || ''}
                    onChange={(e) => setCustomFields({ ...customFields, bride_name: e.target.value })}
                    placeholder="Enter bride's name"
                    data-testid="bride-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="groom_name">Groom&apos;s Name</Label>
                  <Input
                    id="groom_name"
                    value={customFields.groom_name || ''}
                    onChange={(e) => setCustomFields({ ...customFields, groom_name: e.target.value })}
                    placeholder="Enter groom's name"
                    data-testid="groom-name-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Generate Button */}
          <AIPremiumButton
            featureName="AI stationery generation"
            usageCategory={AI_USAGE_IMAGE_GENERATION}
            className="w-full bg-[var(--theme-primary,#5D7061)] hover:opacity-90"
            onClick={handleGenerate}
            data-testid="generate-stationery-btn"
          >
            Generate {selectedTypes.length} Item{selectedTypes.length !== 1 ? 's' : ''}
          </AIPremiumButton>
        </CardContent>
      </Card>

      {/* Generated Items */}
      {generatedItems.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Generated Stationery</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {generatedItems.map((item) => {
                const Icon = stationeryIcons[item.type.split('_')[0] === 'day' ? 'day_invitation' : item.type] || Envelope;
                const fullImageUrl = item.image_url?.startsWith('/') 
                  ? `${process.env.REACT_APP_BACKEND_URL}${item.image_url}` 
                  : item.image_url;
                
                return (
                  <div key={item.id} className="border rounded-lg overflow-hidden">
                    {/* Image Preview */}
                    {item.image_url ? (
                      <div className="aspect-[4/5] bg-gray-100 relative group">
                        <img
                          src={fullImageUrl}
                          alt={item.name}
                          className="w-full h-full object-contain"
                        />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="sm"
                                variant="secondary"
                                title="Download"
                              >
                                <Download className="w-4 h-4" />
                                <CaretDown className="w-3 h-3 ml-1" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuItem onClick={() => handleDownload(item, 'png')}>
                                <FileImage className="w-4 h-4 mr-2" />
                                PNG (Original)
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDownload(item, 'png-hires')}>
                                <FileImage className="w-4 h-4 mr-2" />
                                PNG (300 DPI Print)
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDownload(item, 'pdf')}>
                                <FilePdf className="w-4 h-4 mr-2" />
                                PDF (Print Ready)
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => handleEditItem(item)}
                            title="Edit & Regenerate"
                          >
                            <PencilSimple className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="aspect-[4/5] bg-gray-100 flex items-center justify-center">
                        <ImageIcon className="w-12 h-12 text-gray-300" />
                      </div>
                    )}
                    
                    {/* Info */}
                    <div className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium text-sm">{item.name}</span>
                        </div>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => handleDeleteItem(item.id)}
                        >
                          <Trash className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      {/* Editable Fields Summary */}
                      {item.editable_fields && Object.keys(item.editable_fields).length > 0 && (
                        <div className="mt-2 text-xs text-muted-foreground">
                          {Object.entries(item.editable_fields).slice(0, 2).map(([key, value]) => (
                            value && <div key={key} className="truncate">{fieldLabels[key] || key}: {value}</div>
                          ))}
                        </div>
                      )}
                      
                      {/* Action Buttons */}
                      <div className="flex gap-2 mt-3">
                        {item.image_url && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                className="flex-1"
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Download
                                <CaretDown className="w-3 h-3 ml-1" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuItem onClick={() => handleDownload(item, 'png')}>
                                <FileImage className="w-4 h-4 mr-2" />
                                PNG (Original)
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDownload(item, 'png-hires')}>
                                <FileImage className="w-4 h-4 mr-2" />
                                PNG (300 DPI)
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDownload(item, 'pdf')}>
                                <FilePdf className="w-4 h-4 mr-2" />
                                PDF (Print Ready)
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          onClick={() => handleEditItem(item)}
                        >
                          <PencilSimple className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editingItem} onOpenChange={(open) => !open && handleCloseEdit()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PencilSimple className="w-5 h-5" />
              Edit {editingItem?.name}
            </DialogTitle>
          </DialogHeader>
          
          {editingItem && (
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                Update the fields below and regenerate your stationery with the new content.
              </p>
              
              {/* Current Image Preview */}
              {editingItem.image_url && (
                <div className="mb-4">
                  <Label className="text-xs text-muted-foreground">Current Design</Label>
                  <img 
                    src={editingItem.image_url.startsWith('/') 
                      ? `${process.env.REACT_APP_BACKEND_URL}${editingItem.image_url}` 
                      : editingItem.image_url} 
                    alt={editingItem.name}
                    className="w-full h-32 object-contain rounded-lg border mt-1"
                  />
                </div>
              )}
              
              {/* Editable Fields */}
              <div className="space-y-3">
                {editingItem.required_fields?.map((field) => (
                  <div key={field}>
                    <Label htmlFor={`edit-${field}`}>{fieldLabels[field] || field}</Label>
                    <Input
                      id={`edit-${field}`}
                      value={editFields[field] || ''}
                      onChange={(e) => setEditFields({ ...editFields, [field]: e.target.value })}
                      placeholder={`Enter ${fieldLabels[field] || field}`}
                    />
                  </div>
                ))}
                
                {/* Show other editable fields not in required_fields */}
                {Object.keys(editingItem.editable_fields || {})
                  .filter(key => !editingItem.required_fields?.includes(key))
                  .map((field) => (
                    <div key={field}>
                      <Label htmlFor={`edit-${field}`}>{fieldLabels[field] || field}</Label>
                      <Input
                        id={`edit-${field}`}
                        value={editFields[field] || ''}
                        onChange={(e) => setEditFields({ ...editFields, [field]: e.target.value })}
                        placeholder={`Enter ${fieldLabels[field] || field}`}
                      />
                    </div>
                  ))
                }
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseEdit}>
              Cancel
            </Button>
            <AIPremiumButton
              featureName="AI stationery regeneration"
              usageCategory={AI_USAGE_IMAGE_GENERATION}
              className="bg-[var(--theme-primary,#5D7061)]"
              onClick={handleSaveAndRegenerate}
            >
              Save & Regenerate
            </AIPremiumButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
